import { computed, inject, Injectable, signal } from '@angular/core';
import { z } from 'zod';
import { Customer } from '@shared/customer-model-customer';
import { CustomerDtoSchema } from '@shared/customer-data-access-customer';
import { AppStorageService } from '@suite/app-storage-data-access';

const SELECTED_CUSTOMER_KEY = 'selected-customer';
const CUSTOMER_QUERY_HISTORY_KEY = 'customer-query-history';
const MAX_HISTORY_SIZE = 20;

@Injectable({ providedIn: 'root' })
export class CustomerService {
  private readonly appStorageService = inject(AppStorageService);

  private readonly _selectedCustomer = signal<Customer | null>(this.loadSelectedCustomer());
  private readonly _queryHistory = signal<Customer[]>(this.loadQueryHistory());

  readonly selectedCustomer = this._selectedCustomer.asReadonly();
  readonly selectedCustomerId = computed(() => this._selectedCustomer()?.customerID ?? null);
  readonly queryHistory = this._queryHistory.asReadonly();

  setSelectedCustomer(customer: Customer): void {
    this._selectedCustomer.set(customer);
    this.appStorageService.setItem$.next({
      key: SELECTED_CUSTOMER_KEY,
      value: JSON.stringify(customer),
    });
    this.addToQueryHistory(customer);
  }

  clearSelectedCustomer(): void {
    this._selectedCustomer.set(null);
    this.appStorageService.removeItem$.next({
      key: SELECTED_CUSTOMER_KEY,
    });
  }

  private addToQueryHistory(customer: Customer): void {
    const currentHistory = this._queryHistory();
    const filteredHistory = currentHistory.filter((c) => c.customerID !== customer.customerID);
    const newHistory = [customer, ...filteredHistory].slice(0, MAX_HISTORY_SIZE);
    this._queryHistory.set(newHistory);
    this.appStorageService.setItem$.next({
      key: CUSTOMER_QUERY_HISTORY_KEY,
      value: JSON.stringify(newHistory),
    });
  }

  private loadSelectedCustomer(): Customer | null {
    const stored = this.appStorageService.getItem(SELECTED_CUSTOMER_KEY);
    if (!stored) {
      return null;
    }
    try {
      const parsed: unknown = JSON.parse(stored);
      const result = CustomerDtoSchema.safeParse(parsed);
      return result.success ? result.data : null;
    } catch {
      return null;
    }
  }

  private loadQueryHistory(): Customer[] {
    const stored = this.appStorageService.getItem(CUSTOMER_QUERY_HISTORY_KEY);
    if (!stored) {
      return [];
    }
    try {
      const parsed: unknown = JSON.parse(stored);
      const result = z.array(CustomerDtoSchema).safeParse(parsed);
      return result.success ? result.data : [];
    } catch {
      return [];
    }
  }
}
